import React, { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import omitBy from 'lodash/omitBy';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import { writeAfterAuthActionAtom } from 'auth/userStore';
import { useUserDetails } from 'auth/mutations/userHooks';
import { AfterAuthActionType } from 'auth/constants';
import useIncompleteUserProcessing from 'components/auth/hooks/useIncompleteUserProcessing';
import boatsetterPaths from 'utils/boatsetterPaths';
import { isBoatLocationType } from 'components/blw/utils';
import { BoatModel } from 'swagger/models';
import { getAsString, valueEmpty } from 'helpers';
import { iOS } from 'helpers/devices';
import { years, renderLabel, topMakes } from 'helpers/boatModel';
import { areAddressValuesMissing } from 'components/forms/utils/boatFormHelpers';
import getNavigation, { PathType, getBLWPathsWithBoat } from 'components/blw/navigation';
import { FormValues } from './utils/boatFormTypes';
import SelectInput from './SelectInput';
import LocationInput from './LocationInput';
import { TOP_MAKES } from './boat/constants';
import FormFooter from './FormFooter';
import GenericErrorBoundary from '../search/GenericErrorBoundary';
import InputText from './InputText';
import {
  useBoatMakesQuery,
  useBoatModelsQuery,
  useCreateBoatMutation,
  useGetBoatAddressesQuery,
  useUpdateBoatAddressMutation,
  useUpdateBoatMutation,
} from '../blw/hooks';
import { useBoatDetails } from '../pdp/v3/hooks';

// The BoatModel type from the API package has more properties than are in the type.
interface BoatModelWithExtras extends BoatModel {
  trim_id: string;
  // `length` starts as a string, but is coerced to a number
  length: string | number;
}

// `year_manufactured` switches from a string to a number here, thus the omit
// and reintroduction with a new type.
type Payload = Omit<FormValues, 'year_manufactured'> & { year_manufactured: number } & Partial<BoatModelWithExtras>;

interface BoatFormProps {
  smallScreen: boolean;
  showForm: boolean;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  modalRef: React.MutableRefObject<HTMLDivElement>;
}
const BoatForm: React.FC<BoatFormProps> = (props) => {
  const { smallScreen, setShowForm, showForm, modalRef } = props;

  const {
    userDetails: { phone_verified },
    isAuthenticated,
  } = useUserDetails();

  const router = useRouter();

  // It's unclear if anything from this block will ever be defined
  const { boatAddresses } = useGetBoatAddressesQuery(getAsString(router.query.id));
  const { boatDetails } = useBoatDetails(getAsString(router.query.id));
  const { id: boatId, year_manufactured, make, model } = boatDetails;

  const updateBoatAddressMutation = useUpdateBoatAddressMutation();
  const updateBoatMutation = useUpdateBoatMutation();

  const boatLocation = head(boatAddresses) || boatDetails.location || {};

  // tease out values specific to one response or the other
  const locationId = isBoatLocationType(boatLocation) ? undefined : boatLocation.uuid;
  const location_name = isBoatLocationType(boatLocation) ? boatLocation.location_name : undefined;
  const location_type = isBoatLocationType(boatLocation) ? boatLocation.location_type : undefined;
  const full_address = isBoatLocationType(boatLocation) ? boatLocation.full_address : undefined;

  // values in common
  const {
    address,
    slip_number,
    // @ts-expect-error: street_2 appears in neither API response
    street_2,
    city,
    state,
    postal_code,
    country: countryName,
    country_code,
    lat,
    lng,
  } = boatLocation;

  const modelDefaultValue = (label = null) => {
    const modelSelectedFromTrims = models.find((m) => model === m.model);
    if (modelSelectedFromTrims) {
      return label ? renderLabel(model) : model;
    }
    return 'Other';
  };

  const makeDefaultValue = () => {
    const allMakes = [...TOP_MAKES, ...makes];
    const makeSelected = allMakes.includes(make);
    if (makeSelected) {
      return renderLabel(make);
    }
    return 'Other';
  };

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      year_manufactured: year_manufactured ? year_manufactured.toString() : null,
      boat_model: null,
      other_boat_model: null,
      make: null,
      other_make: null,
      location_name,
      location_type,
      slip_number,
      address:
        full_address && countryName
          ? `${full_address}, ${countryName === 'United States' ? 'USA' : countryName}`
          : null,
      street_1: address,
      street_2,
      city,
      state,
      postal_code,
      country_code,
      lat,
      lng,
    },
  });

  const { handleSubmit, register, watch, control, setValue } = methods;

  const pickedMake = useWatch<string>({
    name: 'make',
    control,
  });
  const pickedYear = useWatch<number>({
    name: 'year_manufactured',
    control,
  });

  const { boatModels: models = [] } = useBoatModelsQuery(pickedMake, pickedYear);
  // makes is a string[] despite the any type
  const { boatMakes: makes = [] } = useBoatMakesQuery(pickedYear);

  const AllMakes = () =>
    Array.from(makes as string[], (v) => ({
      value: renderLabel(v),
      label: renderLabel(v),
      group: 'All',
    }));

  const topMakesOptions = [
    { label: 'Top Makes', options: topMakes() },
    { label: 'All', options: AllMakes() },
    {
      label: 'Other',
      options: [{ value: 'Other', label: 'Other', group: 'Other' }],
    },
  ];

  const boatModels = Array.from(models, (v) => ({
    value: v.id,
    label: renderLabel(v.model),
  }));

  const addAfterAuthAction = useSetAtom(writeAfterAuthActionAtom);
  const createBoatMutation = useCreateBoatMutation();
  const { handlePossibleIncompleteAccountInApp } = useIncompleteUserProcessing();

  /**
   * On form submit, react-hook-form calls this function if - IF! -
   * react-hook-form's formState is valid.
   */
  const processFormData: SubmitHandler<FormValues> = async (values) => {
    // FormValues would be a subset of Payload BUT year_manufactured is a number
    // instead of a string, so we spread the values into the new payload object,
    // but explicitly parse year_manufactured into a number
    let payload: Payload = {
      ...values,
      year_manufactured: parseInt(values.year_manufactured, 10),
    };

    const modelSelectedFromTrims = models.find((m) => values.boat_model === m.id) as BoatModelWithExtras;
    if (modelSelectedFromTrims) {
      const trimValues = omitBy(modelSelectedFromTrims, (val) => isEmpty(val));
      payload = { ...payload, ...trimValues };
      payload.trim_id = modelSelectedFromTrims.trim_id;
      payload.boat_model = modelSelectedFromTrims.model;
      if (typeof modelSelectedFromTrims.length === 'string') {
        payload.length = parseInt(modelSelectedFromTrims.length, 10);
      }
    }

    delete payload.id;

    // fold `other_make` into `make`
    if (payload.other_make) {
      payload.make = payload.other_make;
    }

    // fold `other_boat_model` into `boat_model`
    // !!! This doesn't set `model` on the payload.
    if (payload.other_boat_model) {
      payload.boat_model = payload.other_boat_model;
    }

    // Perhaps there's a more targeted way to do this
    const createBoatPayload = {
      ...payload,
      boat: payload,
      address: payload,
    };
    const createBoatAction = {
      type: AfterAuthActionType.CREATE_BOAT,
      apiPayload: createBoatPayload,
      action: () => createBoatMutation.mutateAsync({ body: createBoatPayload }),
    };
    if (!isAuthenticated) {
      addAfterAuthAction(createBoatAction);
      router.push(boatsetterPaths.signUp);
      return false;
    }

    if (isAuthenticated && !phone_verified) {
      addAfterAuthAction(createBoatAction);
      handlePossibleIncompleteAccountInApp('');
      return false;
    }

    let boatCreated;

    setFormSubmitted(true);

    if (boatId) {
      await updateBoatMutation.mutateAsync({
        boatId,
        // If one ever has time, remove the comment below and look at the type
        // error. There's probably some issue there, but time doesn't permit
        // looking at it.
        // @ts-expect-error: This has been here a long time.
        body: payload,
      });
      if (locationId) {
        await updateBoatAddressMutation.mutateAsync({
          boatId,
          addressId: locationId,
          body: {
            address: payload,
          },
        });
      }
      const boatPaths = getBLWPathsWithBoat(boatId);
      const nav = getNavigation(
        {
          currentPath: router.pathname as PathType,
          boatId,
          isReview: !!router.query.review,
        },
        boatPaths
      );
      await router.push(nav.nextPath);
    } else {
      const response = await createBoatMutation.mutateAsync({
        body: createBoatPayload,
      });
      boatCreated = response.data;
      const boatPaths = getBLWPathsWithBoat(boatCreated.id);
      const nav = getNavigation(
        {
          currentPath: router.pathname as PathType,
          boatId: response.data.id,
          isReview: !!router.query.review,
        },
        boatPaths
      );
      await router.push(nav.nextPath);
    }
    const id = boatId || boatCreated.id;
    if (!id) {
      console.error('NO ID', id);
    }
  };

  const valuesMissing = () =>
    areAddressValuesMissing(watch(), methods.errors) ||
    valueEmpty(watch('year_manufactured')) ||
    valueEmpty(watch('make')) ||
    valueEmpty(watch('boat_model')) ||
    (watch('make') === 'Other' && valueEmpty(watch('other_make'))) ||
    (watch('boat_model') === 'Other' && valueEmpty(watch('other_boat_model')));

  const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false);
  const hasErrors = valuesMissing() && hasAttemptedSubmission;
  const [topInputFocus, setTopInputFocus] = useState(false);
  const [topInputFocusAddress, setTopInputFocusAddress] = useState(false);
  const [keyBoardOpen, setKeyBoardOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [orgDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  useEffect(() => {
    const wrapper = document.getElementById('__next');
    if (iOS()) {
      wrapper.classList.add('isIOS');
    }
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerHeight < orgDimensions.height) {
        setKeyBoardOpen(true);
      } else {
        setKeyBoardOpen(false);
      }
    }

    window.addEventListener('resize', handleResize);
  });

  const scrollToBottom = () => {
    const root = document.getElementById('__next');
    if (keyBoardOpen && smallScreen) {
      // @ts-ignore
      setTimeout(root.scrollTo(0, 1500), 200);
    }
  };

  const getStyles = () => {
    if (hasErrors) return { marginBottom: '0px' };
    if (!smallScreen) return { marginBottom: '47px' };
    return {};
  };

  const [resetMake, setResetMake] = useState(0);
  const [resetModel, setResetModel] = useState(0);

  const hasMissingStreetNumber = Boolean(methods.formState.errors.street_number);
  const locationInputErrorMessage = hasMissingStreetNumber
    ? 'Please enter the exact address to continue'
    : 'This field is required';

  const isUS = watch('country_code') === 'US';

  return (
    <div
      ref={modalRef}
      className={`Panel Panel--paddingMedium Panel--sm-noRoundBottom u-sizeFull ${
        topInputFocus && keyBoardOpen ? 'TopInputFocus' : 'BoatForm-enter-done'
      }  ${topInputFocusAddress && keyBoardOpen ? 'TopInputFocusAddress' : 'BoatForm-enter-done'} ${
        watch('make') === 'Other' || watch('boat_model') === 'Other' || hasErrors
          ? 'PanelHeightNone'
          : 'BoatForm-enter-done'
      } ${showForm ? '' : 'Panel--shadow'} `}
      style={getStyles()}
    >
      <p className="u-textSemiBold u-sm-show"> Tell us about your boat </p>
      <button
        style={{ right: '25px', top: '22px' }}
        className="u-floatRight u-posAbsolute u-sm-show"
        type="button"
        onClick={() => {
          setShowForm(false);
        }}
      >
        <svg height={20} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1008 1008">
          <path d="M227.8 157.3l633.9 633.9c19.5 19.5 19.5 51 0 70.4-19.5 19.5-51 19.5-70.4 0l-634-633.8c-19.5-19.5-19.5-51 0-70.4 19.5-19.5 51-19.5 70.5-.1z" />
          <path d="M146.3 780.2l633.9-633.9c19.5-19.5 51-19.5 70.4 0 19.5 19.5 19.5 51 0 70.4l-633.9 634c-19.5 19.5-51 19.5-70.4 0-19.5-19.5-19.5-51 0-70.5z" />
        </svg>
      </button>
      <div className="Grid">
        <div className="u-sizeFull">
          <FormProvider {...methods}>
            <form autoComplete="nofill" onSubmit={handleSubmit(processFormData)}>
              <div className="Grid">
                <div className="Grid-cell u-mb2">
                  <GenericErrorBoundary>
                    <LocationInput
                      showError
                      name="address"
                      defaultValue={address}
                      placeholder="Boat address"
                      errorMessage={locationInputErrorMessage}
                      onBlur={() => {
                        setTopInputFocusAddress(false);
                      }}
                      onFocus={() => {
                        setTopInputFocusAddress(true);
                      }}
                    />
                  </GenericErrorBoundary>
                  <input type="hidden" name="location_name" id="location_name" ref={register} />
                  <input
                    type="hidden"
                    name="street_number"
                    id="street_number"
                    ref={register({
                      required: isUS ? 'Required' : false,
                      validate: (value) => (isUS ? !valueEmpty(value) : true),
                    })}
                  />
                  <input
                    type="hidden"
                    name="street_1"
                    id="street_1"
                    ref={register({
                      validate: (value) => !valueEmpty(value),
                    })}
                  />
                  <input type="hidden" name="street_2" id="street_2" ref={register} />
                  <input
                    type="hidden"
                    name="city"
                    id="city"
                    ref={register({ required: 'Required', validate: (value) => !valueEmpty(value) })}
                  />
                  <input type="hidden" name="state" id="state" ref={register({ required: 'Required' })} />
                  <input type="hidden" name="postal_code" id="postal_code" ref={register({ required: 'Required' })} />
                  <input type="hidden" name="country_code" id="country_code" ref={register({ required: 'Required' })} />
                </div>
              </div>
              <div className="Grid Grid--withGutter Grid-sm---noGutter">
                <div className="Grid-cell u-lg-size1of2 u-mb2">
                  <SelectInput
                    name="year_manufactured"
                    defaultValue={year_manufactured}
                    labelName="Boat year"
                    options={years()}
                    onChange={() => {
                      setValue('make', '');
                      setValue('model', '');
                      setResetMake(resetMake + 1);
                      setResetModel(resetModel + 1);
                    }}
                    showError
                    placeholder="Year"
                    onBlur={() => {
                      setTopInputFocus(false);
                    }}
                    onFocus={() => {
                      scrollToBottom();
                      setTopInputFocus(true);
                    }}
                  />
                </div>
                <div className="Grid-cell u-lg-size1of2 u-mb2">
                  <SelectInput
                    name="make"
                    labelName="Boat make"
                    defaultValue={makeDefaultValue()}
                    options={topMakesOptions}
                    onChange={() => {
                      setValue('model', '');
                      setResetModel(resetModel + 1);
                    }}
                    allowCreate={false}
                    isDisabled={!watch('year_manufactured')}
                    showError
                    resetValue={resetMake}
                    placeholder="Make"
                    onFocus={() => {
                      scrollToBottom();
                    }}
                  />
                </div>
                {watch('make') === 'Other' ? (
                  <div className="Grid-cell u-mb2">
                    <InputText
                      label="Enter make"
                      name="other_make"
                      type="text"
                      bottomSpacing={false}
                      id="other_make"
                      onFocus={() => {
                        scrollToBottom();
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <div className="Grid">
                {watch('make') !== 'Other' && (
                  <div className={`Grid-cell ${watch('boat_model') === 'Other' ? 'u-mb2' : 'u-mb2_5'}`}>
                    <SelectInput
                      name="boat_model"
                      defaultValue={modelDefaultValue()}
                      labelName="Boat model"
                      defaultLabel={modelDefaultValue(true)}
                      options={[...boatModels, { value: 'Other', label: 'Other' }]}
                      allowCreate={false}
                      isDisabled={
                        !(watch('year_manufactured') && watch('make')) ||
                        (watch('make') === 'Other' && !watch('other_make'))
                      }
                      resetValue={resetModel}
                      showError
                      placeholder="Model"
                      onFocus={() => {
                        scrollToBottom();
                      }}
                    />
                  </div>
                )}

                {watch('boat_model') === 'Other' || watch('make') === 'Other' ? (
                  <div className="Grid-cell u-mb2_5">
                    <InputText
                      label="Enter model"
                      name="other_boat_model"
                      type="text"
                      bottomSpacing={false}
                      id="other_boat_model"
                      onFocus={() => {
                        scrollToBottom();
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <div className="Divider" />
              <FormFooter
                valuesMissing={valuesMissing}
                hasSubmitted={hasAttemptedSubmission}
                setHasSubmitted={setHasAttemptedSubmission}
                formSubmitted={formSubmitted}
              />
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default BoatForm;
